import cn from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useState, useRef } from 'react'

import { BackButton } from '../BackButton'
import { Backdrop } from '../Backdrop/Backdrop'
import { View } from '../View'
import styles from './FullScreenModal.module.scss'

interface FullScreenModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  backgroundType?: 'backdrop' | 'black'
}

export function FullScreenModal({
  isOpen,
  onClose,
  children,
  backgroundType = 'black',
}: FullScreenModalProps) {
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [isAnimating, setIsAnimating] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) {
      return setIsAnimating(false)
    }

    setShouldRender(true)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsAnimating(true)
      })
    })
  }, [isOpen])

  useEffect(() => {
    const modal = modalRef.current
    if (!modal) return

    const handleTransitionEnd = () => {
      if (!isOpen) {
        setShouldRender(false)
      }
    }

    modal.addEventListener('transitionend', handleTransitionEnd)
    return () => {
      modal.removeEventListener('transitionend', handleTransitionEnd)
    }
  }, [isOpen])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!shouldRender) {
    return null
  }

  if (backgroundType === 'backdrop') {
    return (
      <AnimatePresence onExitComplete={() => setShouldRender(false)}>
        {isOpen && (
          <>
            <BackButton onClick={onClose} />
            <Backdrop onClick={onClose} className={styles.backdropRoot}>
              <motion.div
                className={cn(styles.modal, styles.backdropContainer)}
                initial={{ opacity: 0, scale: 0.45 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                {children}
              </motion.div>
            </Backdrop>
          </>
        )}
      </AnimatePresence>
    )
  }

  return (
    <div className={cn(styles.overlay, { [styles.open]: isOpen })}>
      <View fadeIn className={styles.view}>
        <BackButton onClick={onClose} />
        <div
          ref={modalRef}
          className={cn(styles.modal, {
            [styles.slideIn]: isAnimating,
            [styles.slideOut]: !isOpen,
          })}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      </View>
    </div>
  )
}
